import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MARK_LIMITS, PROFILE_ROLES, contrastOnWhite, type AppSettings, type MahalluProfile, type ProfilePatch } from '@mahallu/shared';
import { ApiError, loadErrorKey, markUrl, settings, useAppSettings, useAudit, useMe, useProfile } from '../api';
import { FileField, FormSection, SelectField, TextField } from '../components/Form';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

function useCanChange() {
  const me = useMe();
  return Boolean(me.data?.role && PROFILE_ROLES.includes(me.data.role));
}

function ProfileForm({ p }: { p: MahalluProfile }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canChange = useCanChange();
  const [f, setF] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(p).filter(([k]) => !['markHash', 'version'].includes(k)).map(([k, v]) => [k, (v as string | null) ?? ''])),
  );
  const set = (k: string) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () => settings.saveProfile({ ...(Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim() || null])) as Omit<ProfilePatch, 'version'>), nameEn: f.nameEn?.trim() || p.nameEn, version: p.version }),
    onSuccess: (x) => {
      qc.setQueryData(['profile'], x);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const colour = /^#[0-9a-fA-F]{6}$/.test(f.colour ?? '') ? f.colour! : null;
  const ratio = colour ? contrastOnWhite(colour.toLowerCase()) : null;
  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <fieldset disabled={!canChange} style={{ border: 0, padding: 0, margin: 0 }}>
        <FormSection title={t('profile.identity')}>
          <TextField label={t('profile.nameEn')} value={f.nameEn ?? ''} onChange={set('nameEn')} maxLength={120} required />
          <TextField label={t('profile.nameMl')} value={f.nameMl ?? ''} onChange={set('nameMl')} maxLength={120} lang="ml" />
          <TextField label={t('profile.addressEn')} value={f.addressEn ?? ''} onChange={set('addressEn')} maxLength={200} full />
          <TextField label={t('profile.addressMl')} value={f.addressMl ?? ''} onChange={set('addressMl')} maxLength={200} lang="ml" full />
          <TextField label={t('profile.district')} value={f.district ?? ''} onChange={set('district')} maxLength={60} />
          <TextField label={t('profile.pin')} value={f.pinCode ?? ''} onChange={set('pinCode')} maxLength={6} inputMode="numeric" />
          <TextField label={t('profile.phone')} value={f.phone ?? ''} onChange={set('phone')} maxLength={30} inputMode="tel" />
          <TextField label={t('profile.email')} value={f.email ?? ''} onChange={set('email')} maxLength={254} />
        </FormSection>
        <FormSection title={t('profile.registrations')}>
          <TextField label={t('profile.waqf')} value={f.waqfRegNo ?? ''} onChange={set('waqfRegNo')} maxLength={60} hint={t('profile.waqfHint')} />
          <TextField label={t('profile.reg12a')} value={f.reg12a ?? ''} onChange={set('reg12a')} maxLength={60} />
          <TextField label={t('profile.reg80g')} value={f.reg80g ?? ''} onChange={set('reg80g')} maxLength={60} hint={t('profile.taxHint')} />
        </FormSection>
        <FormSection title={t('profile.colour')}>
          <TextField label={t('profile.colourHex')} value={f.colour ?? ''} onChange={set('colour')} maxLength={7} hint={t('profile.colourHint')} />
          <div className="fld">
            <span className="note">{t('profile.preview')}</span>
            <span>
              {colour ? <span className="colour-swatch" style={{ background: colour }} /> : null}{' '}
              {ratio !== null ? <Badge tone={ratio >= 4.5 ? 'ok' : 'bad'}>{ratio.toFixed(1)} : 1</Badge> : null}
            </span>
          </div>
        </FormSection>
      </fieldset>
      {canChange ? (
        <div className="action-row">
          <button type="submit" className="btn primary sm" disabled={save.isPending}>
            {t('form.save')}
          </button>
          {save.isError ? <span className="form-error" role="alert">{errorText(save.error)}</span> : save.isSuccess ? <span className="note">{t('seq.saved')}</span> : null}
        </div>
      ) : null}
    </form>
  );
}

function MarkCard({ p }: { p: MahalluProfile }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe();
  const canChange = useCanChange();
  const [file, setFile] = useState<File | null>(null);
  const done = (x: MahalluProfile) => qc.setQueryData(['profile'], x);
  const upload = useMutation({ mutationFn: () => settings.uploadMark(file!), onSuccess: done });
  const remove = useMutation({ mutationFn: settings.removeMark, onSuccess: done });
  const slug = me.data?.mahallu?.slug;
  return (
    <section className="card">
      <div className="section-h">
        <h2>{t('profile.mark')}</h2>
      </div>
      <p className="note">{t('profile.markHint', { kb: MARK_LIMITS.bytes / 1024 })}</p>
      {p.markHash && slug ? <img src={markUrl(slug, p.markHash)} alt={t('profile.mark')} style={{ maxHeight: 96, maxWidth: 240, margin: '8px 0' }} /> : <p className="note">{t('profile.noMark')}</p>}
      {canChange ? (
        <div className="action-row">
          <FileField label={t('profile.markFile')} accept="image/png,image/jpeg" onChange={setFile} />
          <button type="button" className="btn sm primary" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>
            {t('profile.upload')}
          </button>
          {p.markHash ? (
            <button type="button" className="btn sm" disabled={remove.isPending} onClick={() => remove.mutate()}>
              {t('profile.remove')}
            </button>
          ) : null}
          {upload.isError ? <span className="form-error">{errorText(upload.error)}</span> : null}
        </div>
      ) : null}
    </section>
  );
}

/** Settings › Mahallu profile (pilot slice 11): name, address, registrations, own colour and mark. */
export function MahalluProfilePage() {
  const { t } = useTranslation();
  const profile = useProfile();
  return (
    <>
      <PageHeader icon="building" title={t('profile.title')} desc={t('profile.desc')} />
      {profile.isError ? <p className="form-error">{t(loadErrorKey(profile.error))}</p> : null}
      {profile.data ? (
        <div className="privacy-grid">
          <ProfileForm key={profile.data.version ?? 0} p={profile.data} />
          <MarkCard p={profile.data} />
        </div>
      ) : null}
    </>
  );
}

function SettingsForm({ s }: { s: AppSettings }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canChange = useCanChange();
  const [lang, setLang] = useState<'ML' | 'EN'>(s.messageLang);
  const [notifyDefault, setNotifyDefault] = useState(s.receiptNotifyDefault);
  const [sw, setSw] = useState(s.switches);
  const save = useMutation({
    mutationFn: () => settings.saveApp({ messageLang: lang, receiptNotifyDefault: notifyDefault, switches: sw.filter((x) => !x.locked).map((x) => ({ code: x.code, on: x.on, sms: x.sms })), version: s.version || null }),
    onSuccess: (x) => qc.setQueryData(['appSettings'], x),
  });
  const toggle = (code: string, k: 'on' | 'sms') => setSw((list) => list.map((x) => (x.code === code ? { ...x, [k]: !x[k] } : x)));
  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <fieldset disabled={!canChange} style={{ border: 0, padding: 0, margin: 0 }}>
        <FormSection title={t('appset.messages')}>
          <SelectField<'ML' | 'EN'> label={t('appset.lang')} value={lang} onChange={(v) => v && setLang(v)} options={[{ value: 'ML', label: 'മലയാളം' }, { value: 'EN', label: 'English' }]} hint={t('appset.langHint')} />
          <div className="fld">
            <label className="check">
              <input type="checkbox" checked={notifyDefault} onChange={(e) => setNotifyDefault(e.target.checked)} />
              {t('appset.notifyDefault')}
            </label>
          </div>
        </FormSection>
        <div className="tbl-wrap">
          <div className="tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t('delivery.what')}</th>
                  <th>{t('appset.on')}</th>
                  <th>{t('appset.sms')}</th>
                </tr>
              </thead>
              <tbody>
                {sw.map((x) => (
                  <tr key={x.code}>
                    <td>
                      {t(`template.${x.code}`)} {x.locked ? <Badge tone="info">{t('appset.locked')}</Badge> : null}
                    </td>
                    <td>
                      <input type="checkbox" checked={x.on} disabled={x.locked} aria-label={`${t('appset.on')}: ${t(`template.${x.code}`)}`} onChange={() => toggle(x.code, 'on')} />
                    </td>
                    <td>
                      {x.smsPossible ? (
                        <input type="checkbox" checked={x.sms} disabled={x.locked || !x.on} aria-label={`${t('appset.sms')}: ${t(`template.${x.code}`)}`} onChange={() => toggle(x.code, 'sms')} />
                      ) : (
                        <span className="note">{t('appset.whatsappOnly')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="note">{t('appset.lockedHint')}</p>
      </fieldset>
      {canChange ? (
        <div className="action-row">
          <button type="submit" className="btn primary sm" disabled={save.isPending}>
            {t('form.save')}
          </button>
          {save.isError ? <span className="form-error" role="alert">{errorText(save.error)}</span> : save.isSuccess ? <span className="note">{t('seq.saved')}</span> : null}
        </div>
      ) : null}
    </form>
  );
}

/** Settings › App settings (pilot slice 11): message language and the switches per message. */
export function AppSettingsPage() {
  const { t } = useTranslation();
  const s = useAppSettings();
  return (
    <>
      <PageHeader icon="gear" title={t('appset.title')} desc={t('appset.desc')} />
      {s.isError ? <p className="form-error">{t(loadErrorKey(s.error))}</p> : null}
      {s.data ? <SettingsForm key={s.data.version} s={s.data} /> : null}
    </>
  );
}

/** Settings › Audit log (pilot slice 11, MST-11): every change, who made it and when. Append-only. */
export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [f, setF] = useState({ action: '', from: '', to: '', q: '', page: 1 });
  const list = useAudit({ ...f, size: 50 });
  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.size)) : 1;
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v, page: 1 }));
  return (
    <>
      <PageHeader icon="ledger" title={t('auditlog.title')} desc={t('auditlog.desc')} />
      <div className="filters receipt-filters">
        <SelectField label={t('auditlog.action')} value={f.action} onChange={set('action')} empty={t('auditlog.all')} options={(d?.actions ?? []).map((a) => ({ value: a, label: a }))} />
        <TextField label={t('rcpt.from')} type="date" value={f.from} onChange={set('from')} />
        <TextField label={t('rcpt.to')} type="date" value={f.to} onChange={set('to')} />
        <TextField label={t('auditlog.search')} type="search" value={f.q} onChange={set('q')} maxLength={80} />
      </div>
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      {d ? <p className="note">{t('auditlog.count', { n: d.total })}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('delivery.when')}</th>
                <th>{t('auditlog.who')}</th>
                <th>{t('auditlog.action')}</th>
                <th>{t('auditlog.what')}</th>
              </tr>
            </thead>
            <tbody>
              {(d?.rows ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="num">{dateTime(r.at, i18n.language)}</td>
                  <td>
                    {r.actor ?? '—'}
                    {r.actorRole ? <div className="note">{t(`role.${r.actorRole}`)}</div> : null}
                  </td>
                  <td className="num">{r.action}</td>
                  <td>
                    <span className="note">{r.subjectType}{r.subjectId ? ` · ${r.subjectId}` : ''}</span>
                    {Object.keys(r.detail).length ? <div className="note num" style={{ wordBreak: 'break-word' }}>{JSON.stringify(r.detail).slice(0, 240)}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {pages > 1 ? (
        <div className="action-row">
          <button type="button" className="btn sm" disabled={f.page <= 1} onClick={() => setF((x) => ({ ...x, page: x.page - 1 }))}>
            {t('rcpt.prev')}
          </button>
          <span className="note">{t('rcpt.page', { page: f.page, pages })}</span>
          <button type="button" className="btn sm" disabled={f.page >= pages} onClick={() => setF((x) => ({ ...x, page: x.page + 1 }))}>
            {t('rcpt.next')}
          </button>
        </div>
      ) : null}
    </>
  );
}
